import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SecurityContext, SimpleChanges, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { EditorComponent } from '@tinymce/tinymce-angular';
import { broadcastEvents } from 'common/common/constants/broadcast-events';
import { RxEmitter } from 'common/rxjs/rx-emitter';
import { Subject, scan } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { Editor, EditorSettings } from 'tinymce';
import { KeyboardKey } from 'common/common/constants/keyboard-key.enum';
import { ContentFormat } from './content-format.model';
import { GlobalKeyboardShortcutsService } from 'common/keyboard-service/global-keyboard-shortcuts.service';
import 'tinymce/icons/default';
import { BcConfigService } from 'common/config/provider';
import { GenesysSmartAdvisorMessageService } from 'shared/services/genesys-smart-advisor-message/genesys-smart-advisor-message.service';
import { GenesysUrlReplaceService } from 'shared/services/genesys-url-replace/genesys-url-replace.service';
import { DomSanitizer } from '@angular/platform-browser';
import { GenesysTinyMCEPluginsService } from './genesys-tinymce-plugins.service';
@Component({
  selector: 'app-genesys-editor',
  templateUrl: './genesys-editor.component.html',
  styleUrls: ['./genesys-editor.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GenesysEditorComponent implements OnInit, OnDestroy, OnChanges {
  private static readonly DefaultContentFormat: ContentFormat = 'html';

  @Input()
  editorDisabled: boolean;

  @Input()
  showQuoteButton: boolean;

  @Input()
  editorContent: string;

  @Input()
  editorSettings: Partial<EditorSettings>;

  @Input()
  submitOnEnter: boolean;

  @Input()
  contentFormat?: ContentFormat;

  @Input()
  placeholder?: string;

  @Input()
  showFileUpload?: boolean;

  @Input()
  isUrlImagesEnabled?: boolean;

  @Output()
  editorContentChange = new EventEmitter<string>();

  @Output()
  editorSetupDone = new EventEmitter<Editor>();

  @Output()
  quoteButtonClick = new EventEmitter<void>();

  @Output()
  submit = new EventEmitter<string>();

  @Output()
  init = new EventEmitter<void>();

  @Input()
  fileSelectionDisabled: boolean;

  @Output()
  fileSelected = new EventEmitter<File>();

  selectedFile: File;

  @ViewChild(EditorComponent, { static: true }) tinyMceEditor: EditorComponent;
  @ViewChild('fileSelectButton', { static: true }) fileSelectButton: ElementRef<HTMLButtonElement>;

  private editorInstance: Editor;
  private currentLang = this.translateService.currentLang?.toLowerCase() || 'en-us';
  private readonly onDestroySubject = new Subject<void>();

  readonly maxFileSize = 25000000; // ~25MB

  private readonly _showInsertImageDialogSubject = new Subject<void>();
  readonly showInsertImageDialog$ = this._showInsertImageDialogSubject.asObservable().pipe(
    scan((acc) => !acc, false)
  );

  private readonly defaultEditorSettings: Partial<EditorSettings> = {
    branding: false,
    plugins: 'lists advlist link autolink',
    default_link_target: '_blank',
    menubar: false,
    statusbar: false,
    toolbar: 'bold italic underline link',
    forced_root_block: 'p',
    content_css: this.configService.constructAssetUrl('assets/tinymce-style.css'),
    language: this.currentLang,
    language_url: this.configService.constructAssetUrl(`langs/${this.currentLang}.js`),
    theme_url: this.configService.constructAssetUrl('themes/silver/theme.js'),
    skin_url: this.configService.constructAssetUrl('skins/ui/oxide'),
    icons: 'bold-tinymce-icons',
    icons_url: this.configService.constructAssetUrl('icons/bold-tinymce-icons/icons.js'),
    browser_spellcheck: true,
    custom_colors: false,
    contextmenu: false,
    height: '100%',
    setup: editor => {
      this.editorInstance = editor;
      this.editorInstance.on('init', () => this.onEditorInitialized());
      this.editorInstance.on('keyDown', e => this.onEditorKeyDown(e));
      this.pluginService.addDynamicFileUploadButton(this.editorInstance, true);
      this.pluginService.addInsertImagePlugin(this.editorInstance);
      this._subscribeToInsertImageEvent(editor);
      this.editorSetupDone.emit(editor);
    }
  };

  constructor(private readonly translateService: TranslateService,
              private readonly rxEmitter: RxEmitter,
              private readonly globalKeyboardService: GlobalKeyboardShortcutsService,
              private readonly genesysSmartAdvisorMessagingService: GenesysSmartAdvisorMessageService,
              private readonly configService: BcConfigService,
              private readonly sanitizer: DomSanitizer,
              private readonly pluginService: GenesysTinyMCEPluginsService
  ) {
    this.rxEmitter.getNamespaceObservable(broadcastEvents.focusEditor).pipe(
      takeUntil(this.onDestroySubject)
    ).subscribe(() => this.setFocusToEndOfEditorText());

    this.genesysSmartAdvisorMessagingService.copyPasteEvent$.pipe(
      takeUntil(this.onDestroySubject)
    ).subscribe(content => {
      const replacedContent = this.isUrlImagesEnabled ? content : GenesysUrlReplaceService.replaceImageWithUrl(content);
      if(this.editorContent){
        this.editorContent += this.sanitizer.sanitize(SecurityContext.HTML, replacedContent);
      } else {
        this.editorContent = this.sanitizer.sanitize(SecurityContext.HTML, replacedContent);
      }
      this.editorContentChange.emit(this.editorContent);
      this.setFocusToEndOfEditorText();
    });
  }

  ngOnInit(): void {
    this.tinyMceEditor.init = {
      ...this.defaultEditorSettings,
      ...this.editorSettings,
      placeholder: this.placeholder
    };

    if(this.showFileUpload){
      this.rxEmitter.getNamespaceObservable(broadcastEvents.fileUploadOpened).pipe(
        takeUntil(this.onDestroySubject)
      ).subscribe(() => {
        this.selectedFile = null;
        this.fileSelectButton.nativeElement.click();
      });
    }

    this.showInsertImageDialog$.pipe(
      filter((showInsertImageDialog) => !showInsertImageDialog),
      takeUntil(this.onDestroySubject)
    ).subscribe(() => this.focusEditor());
  }

  ngOnDestroy(): void {
    this.onDestroySubject.next();
    this.onDestroySubject.complete();
  }

  ngOnChanges({ placeholder, fileSelectionDisabled }: SimpleChanges) {
    if (placeholder && !placeholder.firstChange) {
      this.editorInstance.settings.placeholder = placeholder.currentValue;
    }

    if (fileSelectionDisabled && !fileSelectionDisabled.firstChange) {
      this.pluginService.setFileUploadButtonDisabled(this.editorInstance, fileSelectionDisabled.currentValue);
    }
  }

  handleInnerChange(content: string): void {
    if (this.editorContent !== content) {
      const replacedContent = this.isUrlImagesEnabled ? content : GenesysUrlReplaceService.replaceImageWithUrl(content);

      this.editorContentChange.emit(replacedContent);
    }
  }

  toggleImageLinkDialog(): void {
    this._showInsertImageDialogSubject.next();
  }

  insertImageLink(image: string): void {
      const imageLink = `<img src="${image}">`;
      this.editorInstance.insertContent(imageLink);

      this.toggleImageLinkDialog();
  }

  private onEditorInitialized(): void {
    this.globalKeyboardService.registerGlobalKeyboardShortcutsToElement(this.editorInstance.getBody());
    this.init.emit();
  }

  private setFocusToEndOfEditorText(): void {
    setTimeout(() => {
      this.focusEditor();
      if (this.editorInstance && this.editorInstance.selection) {
        this.editorInstance.selection.select(this.editorInstance.getBody(), true);
        this.editorInstance.selection.collapse(false);
      }
    });
  }

  private focusEditor(): void {
    if (this.editorInstance) {
      this.editorInstance.focus();
    }
  }

  private _subscribeToInsertImageEvent(editor: Editor): void {
    this.rxEmitter
        .getNamespaceObservable(`${broadcastEvents.fileUploadOpened}${editor.id}`)
        .pipe(takeUntil(this.onDestroySubject))
        .subscribe(() => {
          this.toggleImageLinkDialog();
        });
  }

  private onEditorKeyDown(event: KeyboardEvent): void {
    if (event.key === KeyboardKey.Enter && !event.shiftKey && this.submitOnEnter) {
      event.preventDefault();
      event.stopPropagation();
      this.convertToHyperlink();
      this.submit.emit(this.getFormattedContent());
    }
  }

  private convertToHyperlink() {
    // If the content is only a URL, convert it to a hyperlink
    const editorText = this.editorInstance.getContent({ format: 'text'});
    const regex = /^(https?:\/\/(www\.)?|www\.)[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)(\n)?$/g;
    const url = regex.exec(editorText);
    if (url?.length > 0) {
      this.editorInstance.selection.select(this.editorInstance.getBody(), true);
      this.editorInstance.execCommand('createLink', false, url[0].trim());
    }
  }

  private getFormattedContent(): string {
    return this.editorInstance.getContent({ format: this.contentFormat ?? GenesysEditorComponent.DefaultContentFormat });
  }
}
